import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AccessEditor } from '../../components/admin/AccessEditor';
import { Card, Empty, Icon, Kpi, LivePill, StatusPill, Toast, usePoll } from '../../components/admin/bits';
import { ReachBars, SectionBars } from '../../components/admin/Charts';
import { DeleteDialog } from '../../components/admin/DeleteDialog';
import { HtmlInput } from '../../components/admin/HtmlInput';
import { draftOf, pricingBody, PricingFields } from '../../components/admin/PricingFields';
import { RequestItem } from '../../components/admin/Requests';
import { Timeline } from '../../components/admin/Timeline';
import { Dialog } from '../../components/Dialog';
import { api, errorMessage } from '../../lib/api';
import { CAPTURE_LABEL, CONFIDENCE_LABEL, fmtDate, fmtDateTime, fmtDuration, fmtRelative, todayIso } from '../../lib/format';
import { quoteLines } from '../../lib/pricing';
import type { Detail, Pricing } from '../../lib/types';
import { copyLink } from './ProposalList';

const VersionDialog = ({ open, onClose, proposalId, onDone }: { open: boolean; onClose: () => void; proposalId: string; onDone: (n: number) => void }) => {
  const [html, setHtml] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api<{ number: number }>(`/api/admin/proposals/${proposalId}/versions`, { body: { html, note } });
      setHtml('');
      setNote('');
      onDone(r.number);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} kicker="Nova versão" title="Enviar o HTML atualizado">
      <p className="muted">Quem abrir a partir de agora vê a nova versão. As leituras e o aceite continuam ligados à versão que estava no ar.</p>
      <HtmlInput value={html} onChange={setHtml} />
      <label className="field">
        <span>O que mudou</span>
        <input className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: prazo do módulo de cotação revisto" />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="dialog-foot">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancelar
        </button>
        <button className="btn" onClick={save} disabled={busy || !html}>
          {busy ? 'Publicando…' : 'Publicar versão'}
        </button>
      </div>
    </Dialog>
  );
};

const Validity = ({ id, expiresOn, onSaved }: { id: string; expiresOn: string; onSaved: () => void }) => {
  const [value, setValue] = useState(expiresOn);
  useEffect(() => setValue(expiresOn), [expiresOn]);
  const save = async () => {
    await api(`/api/admin/proposals/${id}`, { method: 'PATCH', body: { expiresOn: value } });
    onSaved();
  };
  return (
    <div className="validity">
      <label className="field">
        <span>Válida até</span>
        <input className="input" type="date" min={todayIso(0)} value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      {value !== expiresOn && value && (
        <button className="btn btn-sm" onClick={save}>
          Salvar
        </button>
      )}
    </div>
  );
};

const Prices = ({ id, pricing, onSaved }: { id: string; pricing: Pricing | null; onSaved: () => void }) => {
  const saved = JSON.stringify(draftOf(pricing));
  const [draft, setDraft] = useState(draftOf(pricing));
  const [error, setError] = useState('');
  useEffect(() => setDraft(JSON.parse(saved)), [saved]);
  const save = async () => {
    const body = pricingBody(draft);
    if ('error' in body) return setError(body.error);
    setError('');
    try {
      await api(`/api/admin/proposals/${id}`, { method: 'PATCH', body });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <>
      <PricingFields value={draft} onChange={setDraft} />
      {error && <p className="error">{error}</p>}
      {JSON.stringify(draft) !== saved && (
        <div className="dialog-foot">
          <button className="btn btn-sm" onClick={save}>
            Salvar valores
          </button>
        </div>
      )}
    </>
  );
};

export const ProposalDetail = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const load = useCallback(() => api<Detail>(`/api/admin/proposals/${id}`), [id]);
  const { data, error, reload } = usePoll(load, 15_000);
  const [toast, setToast] = useState('');
  const [dialog, setDialog] = useState<'version' | 'delete' | null>(null);
  const [showHandled, setShowHandled] = useState(false);

  if (!data) return error ? <p className="error">{error}</p> : <div className="center"><div className="spinner" /></div>;
  const { proposal: p } = data;
  const accept = data.responses.find((r) => r.kind === 'accept');
  const requests = data.responses.filter((r) => r.kind !== 'accept' && (showHandled || !r.handledAt));
  const handledCount = data.responses.filter((r) => r.kind !== 'accept' && r.handledAt).length;

  return (
    <>
      <div className="ahead">
        <div style={{ minWidth: 0 }}>
          <Link to="/admin/propostas" className="back">
            ← Propostas
          </Link>
          <h1>{p.title}</h1>
          <div className="ahead-meta">
            <StatusPill status={p.status} />
            <LivePill n={p.live} />
            <span className="dim">
              {p.clientName} · v{p.versionNumber} · criada em {fmtDate(p.createdAt)}
            </span>
          </div>
        </div>
        <div className="ahead-act">
          <a className="btn btn-ghost btn-sm" href={`/p/${p.slug}`} target="_blank" rel="noreferrer">
            Ver como cliente
          </a>
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('version')}>
            Nova versão
          </button>
          <button className="btn btn-ghost btn-sm" onClick={async () => setToast(`Link copiado: ${await copyLink(p.slug)}`)}>
            Copiar link
          </button>
          <button className="icon-btn" aria-label="Excluir proposta" onClick={() => setDialog('delete')}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h12M8 6V4h4v2M6 6l.8 10h6.4L14 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="kpis kpis-7">
        <Kpi label="Acessos" value={p.logins} sub="entradas com código" />
        <Kpi label="Aberturas" value={p.opens} sub={p.lastSeenAt ? `última ${fmtRelative(p.lastSeenAt)}` : 'nenhuma ainda'} />
        <Kpi label="Pessoas" value={p.visitors} sub={`${p.accessCount} ${p.accessCount === 1 ? 'acesso liberado' : 'acessos liberados'}`} />
        <Kpi label="Tempo de leitura" value={fmtDuration(p.activeMs)} sub="ativo, aba visível" />
        <Kpi label="Tempo médio" value={fmtDuration(p.avgActiveMs)} sub="por abertura" />
        <Kpi label="Rolagem máxima" value={`${p.maxScroll}%`} sub="da página" />
        <Kpi label="Sinais de captura" value={p.captures} sub="print, impressão, cópia" tone={p.captures > 0 ? 'risk' : undefined} />
      </div>

      {accept && (
        <section className="accepted panel">
          <div className="accepted-ic">
            <Icon name="ok" />
          </div>
          <div className="accepted-tx">
            <p className="kick" style={{ color: 'var(--leaf)' }}>
              Proposta aceita
            </p>
            <h2>
              {accept.name}
              {accept.roleTitle ? `, ${accept.roleTitle}` : ''}
            </h2>
            <p className="dim">
              {accept.email} · {fmtDateTime(accept.createdAt)} · verificação <code>{accept.sha256?.slice(0, 16)}</code>
            </p>
          </div>
          {((accept.selection && accept.selection.length > 0) || accept.pricing) && (
            <ul className="accepted-sel">
              {(accept.selection ?? []).map((s, i) => (
                <li key={i}>
                  {s.label && <span>{s.label}</span>}
                  <strong>{s.value}</strong>
                </li>
              ))}
              {accept.pricing &&
                quoteLines(accept.pricing).map(([k, v]) => (
                  <li key={k} className="accepted-inv">
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </li>
                ))}
            </ul>
          )}
        </section>
      )}

      <div className="grid-2">
        <Card title="Onde o tempo foi gasto" aside={<span className="dim small">tempo ativo por seção</span>}>
          {data.sections.length === 0 ? <Empty>Aparece assim que alguém ler a proposta.</Empty> : <SectionBars sections={data.sections} />}
        </Card>
        <Card title="Até onde chegaram" aside={<span className="dim small">% das aberturas</span>}>
          {data.reach.total === 0 ? <Empty>Nenhuma abertura ainda.</Empty> : <ReachBars reach={data.reach} />}
        </Card>
      </div>

      <Card title="Quem leu">
        {data.viewers.length === 0 ? (
          <Empty>Ninguém abriu ainda.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Pessoa</th>
                  <th className="r">Acessos</th>
                  <th className="r">Aberturas</th>
                  <th className="r">Leitura</th>
                  <th className="r">Rolagem</th>
                  <th>Parou em</th>
                  <th className="r">Capturas</th>
                  <th>Dispositivo</th>
                  <th>Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {data.viewers.map((v) => (
                  <tr key={v.email}>
                    <td>
                      <span className="t-title">{v.email}</span>
                      {v.live && (
                        <span className="pill" data-s="live" style={{ marginLeft: '.5rem' }}>
                          agora
                        </span>
                      )}
                    </td>
                    <td className="r num">{v.logins}</td>
                    <td className="r num">{v.opens}</td>
                    <td className="r num">{fmtDuration(v.activeMs)}</td>
                    <td className="r num">{v.maxScroll}%</td>
                    <td>{v.lastSection ?? '—'}</td>
                    <td className="r num" data-risk={v.captures > 0 || undefined}>
                      {v.captures}
                    </td>
                    <td className="dim">{v.device ?? '—'}</td>
                    <td className="dim">{fmtRelative(v.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid-main">
        <Card title="Linha do tempo">
          <Timeline items={data.timeline} />
        </Card>

        <div className="stack">
          <Card
            title="Pedidos"
            aside={
              handledCount > 0 ? (
                <button className="btn-link small" onClick={() => setShowHandled(!showHandled)}>
                  {showHandled ? 'Ocultar resolvidos' : `Ver resolvidos (${handledCount})`}
                </button>
              ) : undefined
            }
          >
            {requests.length === 0 ? (
              <Empty>Nenhum pedido de ajuste, reunião ou renovação.</Empty>
            ) : (
              <div className="reqs">
                {requests.map((r) => (
                  <RequestItem key={r.id} r={r} onChange={reload} />
                ))}
              </div>
            )}
          </Card>

          <Card title="Sinais de captura">
            {data.captures.length === 0 ? (
              <Empty>Nenhum sinal até agora.</Empty>
            ) : (
              <ul className="caps">
                {data.captures.slice(0, 12).map((c, i) => (
                  <li key={i}>
                    <span className="tl-ic" data-tone="risk">
                      <Icon name="cam" />
                    </span>
                    <div>
                      <p>
                        {CAPTURE_LABEL[c.data.type] ?? 'Sinal'} <span className="dim">· {CONFIDENCE_LABEL[c.data.confidence] ?? ''}</span>
                      </p>
                      <p className="dim small">
                        {c.email} · {fmtDateTime(c.at)}
                        {c.section ? ` · em ${c.section}` : ''}
                      </p>
                      {c.data.text && <p className="cap-text">“{c.data.text}”</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="fine">
              O navegador não informa capturas de tela. Contamos os rastros que elas deixam: tecla Print Screen, atalhos de captura, impressão e cópia de texto. Print no celular não é
              detectável.
            </p>
          </Card>

          <Card title="Valores mensais" aside={<span className="dim small">aparecem na proposta e no aceite</span>}>
            <Prices id={p.id} pricing={p.pricing} onSaved={() => (setToast('Valores atualizados.'), reload())} />
          </Card>

          <Card title="Acesso">
            <Validity id={p.id} expiresOn={p.expiresOn} onSaved={() => (setToast('Validade atualizada.'), reload())} />
            <AccessEditor proposalId={p.id} access={data.access} onChange={reload} notify={setToast} />
          </Card>

          <Card title="Versões">
            <ul className="versions">
              {data.versions.map((v) => (
                <li key={v.id}>
                  <div>
                    <strong>v{v.number}</strong>
                    {v.number === p.versionNumber && <span className="pill" data-s="sent">no ar</span>}
                    {v.note && <span className="muted"> {v.note}</span>}
                  </div>
                  <p className="dim small">
                    {fmtDateTime(v.createdAt)} · {Math.round(v.sizeBytes / 1024)} KB · <code>{v.sha256.slice(0, 12)}</code>
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <VersionDialog
        open={dialog === 'version'}
        onClose={() => setDialog(null)}
        proposalId={p.id}
        onDone={(n) => {
          setDialog(null);
          setToast(`Versão v${n} publicada.`);
          reload();
        }}
      />
      <DeleteDialog proposal={dialog === 'delete' ? p : null} onClose={() => setDialog(null)} onDeleted={() => navigate('/admin/propostas')} />
      {toast && <Toast text={toast} onDone={() => setToast('')} />}
    </>
  );
};
